import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import ClientFormSection from '@afosto/app-components-react/ClientFormSection';
import ItemsFormSection from '../ItemsFormSection';
import TotalsFormSection from '../TotalsFormSection';
import AddressFormSection from '@afosto/app-components-react/AddressFormSection';
import ContactFormSection from '@afosto/app-components-react/ContactFormSection';
import Form from '@afosto/app-components-react/Form';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import OrganisationFormSection from '@afosto/app-components-react/OrganisationFormSection';
import currentTime from '@afosto/utils/date/currentTime';
import getFullName from '@afosto/utils/getFullName';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { projection as projectionApi } from '@afosto/order/api/Projection';
import validationSchema from './validations';
import translations from './translations';

/**
 * Fetch an invoice projection.
 * @param {object} billingAddress
 * @param {object} client
 * @param {object} contact
 * @param {object} organisation
 * @param {array} items
 * @param {array} adjustments
 * @param {object|null} invoice
 * @returns {Promise<null>}
 */
const fetchProjection = async (
  { billingAddress, client, contact, organisation, items = [], adjustments = [] },
  invoice,
) => {
  try {
    const formattedItems = items.reduce((acc, item) => {
      const filteredAdjustments = (item?.adjustments ?? []).filter(
        adjustment => !adjustment?.result?.is_applied_rule,
      );
      const formattedItem = {
        reference: item.reference,
        sku: item.sku,
        description: item.description,
        quantity: item.quantity,
        amount: item.amount,
        adjustments: filteredAdjustments,
      };

      return [...acc, formattedItem];
    }, []);

    const response = await projectionApi.createProjection({
      client_id: client?.client_id ?? null,
      items: formattedItems,
      adjustments,
      contact_id: contact?.id ?? null,
      organisation_id: organisation?.id ?? null,
      address_id: billingAddress?.id ?? null,
      pricing_at: invoice?.pricing_at ?? currentTime(),
    });
    const { data = null } = response;

    return data;
  } catch (err) {
    return null;
  }
};

const InvoiceForm = ({ onSubmit, onCancel, invoice, items }) => {
  const intl = useIntl();
  const [isFetchingProjection, setIsFetchingProjection] = useState(false);
  const [projection, setProjection] = useState({
    items: [],
    adjustments: invoice?.adjustments ?? [],
    vat: [],
    subtotal: 0,
    total: 0,
  });

  const form = useForm({
    validationSchema,
    defaultValues: {
      client: null,
      items,
      adjustments: invoice?.adjustments ?? [],
      billingAddress: null,
      contact: null,
      organisation: null,
    },
    onSubmit: data => {
      if (onSubmit && typeof onSubmit === 'function') {
        onSubmit({ ...data, projection });
      }
    },
    onCancel,
  });
  const { getValues, setValue, watch } = form;
  const clientValue = watch('client');
  const itemsValue = watch('items');
  const adjustmentsValue = watch('adjustments');
  const contactValue = watch('contact');
  const organisationValue = watch('organisation');

  useRegisterInput('items', true, form);
  useRegisterInput('adjustments', true, form);

  useEffect(() => {
    /**
     * Fetch the client data
     * @param {string} clientId
     */
    const fetchClient = async clientId => {
      try {
        const response = await oauthApi.getClient(clientId);
        const { data } = response;

        setValue('client', { ...data, label: data.name, value: data.client_id });
      } catch (err) {
        setValue('client', null);
      }
    };

    /**
     * Fetch the contact data
     * @param {string} contactId
     */
    const fetchContact = async contactId => {
      try {
        const response = await contactsApi.getContact(contactId);
        const { data } = response;

        setValue('contact', { ...data, label: getFullName(data), value: data.id });
      } catch (err) {
        setValue('contact', null);
      }
    };

    /**
     * Fetch the organisation data
     * @param {string} organisationId
     */
    const fetchOrganisation = async organisationId => {
      try {
        const response = await organisationsApi.viewOrganisation(organisationId);
        const { data } = response;

        setValue('organisation', {
          ...data,
          label: data.name,
          description: data.coc_number,
          value: data.id,
        });
      } catch (err) {
        setValue('organisation', null);
      }
    };

    /**
     * Fetch the address data
     * @param {string} addressId
     * @param {string} name
     * @returns {Promise}
     */
    const fetchAddress = async (addressId, name) => {
      try {
        const response = await addressesApi.viewAddress(addressId);
        const { data } = response;

        setValue(name, data);
      } catch (err) {
        setValue(name, null);
      }
    };

    /**
     * Fetch the information that should wait for previous requests.
     * @returns {Promise}
     */
    const fetchDependentInformation = async () => {
      try {
        if (invoice?.customer?.contact?.id) {
          await fetchContact(invoice.customer.contact.id);
        }

        if (invoice?.vendor?.contact?.organisation_id) {
          await fetchOrganisation(invoice.vendor.contact.organisation_id);
        }

        if (invoice?.customer?.contact?.address_id) {
          fetchAddress(invoice.customer.contact.address_id, 'billingAddress');
        }
      } catch (err) {
        // Do something with error.
      }
    };

    if (invoice?.client_id) {
      fetchClient(invoice.client_id);
    }

    fetchDependentInformation();
  }, [invoice, setValue]);

  useEffect(() => {
    let isMounted = true;

    const fetchLatestProjection = async () => {
      try {
        setIsFetchingProjection(true);

        const newProjection = await fetchProjection(getValues(), invoice);

        if (isMounted) {
          setProjection(newProjection);
        }
      } catch (err) {
        if (isMounted) {
          setProjection(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingProjection(false);
        }
      }
    };

    if (itemsValue.length > 0) {
      fetchLatestProjection();
    }

    return () => {
      isMounted = false;
    };
  }, [
    invoice,
    clientValue,
    itemsValue,
    adjustmentsValue,
    getValues,
    contactValue,
    organisationValue,
  ]);

  const handleUpdateContact = updatedContact => {
    setValue('contact', {
      ...updatedContact,
      label: getFullName(updatedContact),
      value: updatedContact.id,
    });
  };

  const handleUpdateOrganisation = updatedOrganisation => {
    setValue('organisation', {
      ...updatedOrganisation,
      label: updatedOrganisation.name,
      value: updatedOrganisation.id,
    });
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form} renderActions={false}>
        <ClientFormSection scopes={['odr:invoices:create']} />
        <ContactFormSection />
        <OrganisationFormSection
          contact={contactValue}
          onUpdateContact={handleUpdateContact}
          autoFillFromContact
        />
        <AddressFormSection
          contact={contactValue}
          setContact={handleUpdateContact}
          organisation={organisationValue}
          setOrganisation={handleUpdateOrganisation}
          type="billing"
          name="billingAddress"
          title={intl.formatMessage(translations.addressFormSection.billing.title)}
          showSetAsBillingAddress={false}
          showSetAsShippingAddress={false}
        />
        <ItemsFormSection projection={projection} isFetchingProjection={isFetchingProjection} />
        <TotalsFormSection projection={projection} isFetchingProjection={isFetchingProjection} />
        <FormActions
          primaryAction={{
            label: intl.formatMessage(translations.formActions.save),
            onClick: form.onSubmit,
            type: 'submit',
            color: 'primary',
            variant: 'contained',
            disabled: itemsValue.length === 0,
          }}
          secondaryAction={
            onCancel
              ? {
                  label: intl.formatMessage(translations.formActions.cancel),
                  onClick: form.onCancel,
                  variant: 'contained',
                }
              : null
          }
        />
      </Form>
    </LoadingWrapper>
  );
};

InvoiceForm.propTypes = {
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  invoice: PropTypes.object,
  items: PropTypes.array,
};

InvoiceForm.defaultProps = {
  onCancel: undefined,
  onSubmit: undefined,
  invoice: null,
  items: [],
};

export default InvoiceForm;
